import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TenantDivisionListItemSchema } from '@/entities/tenants-divisions/api/generated/model';
import { Button, Form, Grid, InputField, SelectField } from '@/shared/ui';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import { type OrganizationsFormValues, organizationsSchema } from './schema';
import { useOrganizationsCreateMutation } from './useOrganizationsCreateMutation';
import { useOrganizationsEditMutation } from './useOrganizationsEditMutation';

type OrganizationsFormProps = {
  fiscalYear: number;
  onClose: () => void;
} & ({ mode: 'create' } | { mode: 'edit'; organization: TenantDivisionListItemSchema });

export const OrganizationsForm: FC<OrganizationsFormProps> = (props) => {
  const { fiscalYear, onClose } = props;
  const { t } = useTranslation();
  const isEdit = props.mode === 'edit';

  const form = useForm<OrganizationsFormValues>({
    resolver: zodResolver(organizationsSchema),
    defaultValues: {
      code: isEdit ? props.organization.code : '',
      name: isEdit ? props.organization.name : '',
      year: String(fiscalYear),
    },
  });

  const yearOptions = [{ label: `${fiscalYear}`, value: fiscalYear }];

  const createMutation = useOrganizationsCreateMutation({ onSuccess: onClose });
  const editMutation = useOrganizationsEditMutation({ onSuccess: onClose });
  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = (values: OrganizationsFormValues) => {
    if (isEdit) {
      editMutation.mutate({
        divisionId: props.organization.id,
        data: {
          code: values.code,
          name: values.name,
        },
      });
    } else {
      createMutation.mutate({
        data: {
          code: values.code,
          name: values.name,
          year: Number(values.year),
        },
      });
    }
  };

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <Grid
            columns={1}
            items={[
              [
                {
                  key: 'year',
                  field: (
                    <SelectField
                      control={form.control}
                      name="year"
                      label={t('master-management.common-table.year')}
                      options={yearOptions}
                      disabled
                    />
                  ),
                },
                {
                  key: 'code',
                  field: (
                    <InputField
                      control={form.control}
                      name="code"
                      label={t('master-management.common-table.code')}
                      required
                      autoComplete={false}
                    />
                  ),
                },
                {
                  key: 'name',
                  field: (
                    <InputField
                      control={form.control}
                      name="name"
                      label={t('master-management.common-table.name')}
                      required
                      autoComplete={false}
                    />
                  ),
                },
              ],
            ]}
          />
        </div>

        <SideMenuFormFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
